import {
  ChangeEvent,
  Children,
  createElement,
  isValidElement,
  useEffect,
  useMemo,
} from "react";
import { Controller } from "react-hook-form";

import {
  InputGroup
} from "../../Inputs/InputGroup/InputGroup.page";
import {
  useDisabledField
} from "../HookForm/HookForm.context";
import {
  InputRadioGroupProps
} from "./InputRadioGroup.types";

export function InputRadioGroup({
  helperText,
  children,
  name,
  rule,
  form,
  ...props
}: InputRadioGroupProps) {

  const isDisabled = useDisabledField(name);
  const fieldDisabled = isDisabled || props.disabled;
  const errorMessage = rule?.required === true
    ? 'Field cannot be empty'
    : (typeof rule?.required === 'string' ? rule.required : undefined);

  const parseValue = (value?: string) => {
    if (value === 'true') {
      return true;
    }
    if (value === 'false') {
      return false;
    }
    return value;
  };

  useEffect(() => {
    if (fieldDisabled || !rule?.required) {
      form.clearErrors(name);
    }
  }, [fieldDisabled, rule?.required, form, name]);

  const computedRules = useMemo(() => {
    if (fieldDisabled) return undefined;
    if (!rule) return undefined;
    const { required, validate, ...rest } = rule;
    if (!required) {
      return { ...rest, ...(validate !== undefined && { validate }) };
    }

    const reqMsg = typeof required === 'string' ? required : 'Field cannot be empty';
    const isSelected = (val: any) =>
      (val !== undefined && val !== null && val !== '') || reqMsg;

    if (typeof validate === 'function') {
      return {
        ...rest,
        validate: (val: any, formValues: any) => {
          const reqCheck = isSelected(val);
          if (reqCheck !== true) return reqCheck;
          return validate(val, formValues);
        }
      };
    }

    if (typeof validate === 'object' && validate !== null) {
      return {
        ...rest,
        validate: {
          required: isSelected,
          ...validate
        }
      };
    }

    return {
      ...rest,
      validate: isSelected
    };
  }, [fieldDisabled, rule?.required, rule?.validate]);

  // Registration merges rules, so clear validators that no longer apply.
  // Lock the radio inputs without disabling Controller, which clears the stored value.
  return (
    <Controller
      control={form.control}
      name={name}
      rules={{ required: false, validate: undefined, ...computedRules }}
      render={({ field, fieldState }) => {
        const isInvalid = !fieldDisabled && fieldState.invalid;
        return (
          <InputGroup
            helperText={isInvalid ? (fieldState.error?.message?.toString() || errorMessage) : helperText}
            required={!fieldDisabled && !!rule?.required}
            error={isInvalid}
            {...props}
          >
            {Children.map(children, (child) => {
              if (isValidElement(child)) {
                const optionDisabled = fieldDisabled || child.props.disabled;
                return createElement(child.type, {
                  ...child.props,
                  name: field.name,
                  onChange: (event: ChangeEvent<HTMLInputElement>) => {
                    if (optionDisabled) {
                      return;
                    }
                    field.onChange(parseValue(event.target.value));
                  },
                  checked: String(field.value ?? '') === String(child.props.value),
                  key: child.key,
                  disabled: optionDisabled,
                  onBlur: field.onBlur,
                });
              }
              return (child);
            })}
          </InputGroup>
        );
      }}
    />
  );
}
